/**
 * Shared audio utilities used by AudioPlayerContext and mediaSessionManager
 */

// Speed options for playback - used by SpeedSelector and player components
export const SPEED_OPTIONS = [
  { value: 0.5, label: '0.5x' },
  { value: 0.6, label: '0.6x' },
  { value: 0.7, label: '0.7x' },
  { value: 0.8, label: '0.8x' },
  { value: 0.9, label: '0.9x' },
  { value: 1, label: '1x' },
  { value: 1.1, label: '1.1x' },
  { value: 1.2, label: '1.2x' },
  { value: 1.3, label: '1.3x' },
  { value: 1.4, label: '1.4x' },
  { value: 1.5, label: '1.5x' },
  { value: 1.6, label: '1.6x' },
  { value: 1.7, label: '1.7x' },
  { value: 1.8, label: '1.8x' },
  { value: 1.9, label: '1.9x' },
  { value: 2, label: '2x' },
];

/**
 * Format seconds to MM:SS or H:MM:SS display format
 */
export function formatTime(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return '0:00';
  if (seconds >= 3600) {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Wait for audio element to be ready to play
 * Fixes Safari/Chrome race conditions with audio loading
 */
export function waitForCanPlay(audio: HTMLAudioElement, timeout = 10000): Promise<void> {
  return new Promise((resolve, reject) => {
    if (audio.readyState >= 2) {
      resolve();
      return;
    }

    const timeoutId = setTimeout(() => {
      cleanup();
      if (audio.readyState >= 1) {
        resolve();
      } else {
        reject(new Error('Audio load timeout'));
      }
    }, timeout);

    const handleReady = () => {
      cleanup();
      resolve();
    };

    const handleError = () => {
      cleanup();
      reject(new Error(audio.error?.message || 'Audio load error'));
    };

    const cleanup = () => {
      clearTimeout(timeoutId);
      audio.removeEventListener('canplaythrough', handleReady);
      audio.removeEventListener('canplay', handleReady);
      audio.removeEventListener('loadeddata', handleReady);
      audio.removeEventListener('error', handleError);
    };

    audio.addEventListener('canplaythrough', handleReady);
    audio.addEventListener('canplay', handleReady);
    audio.addEventListener('loadeddata', handleReady);
    audio.addEventListener('error', handleError);
  });
}
